// Session-expiry experience layer (v2.4) — pure, framework-free helpers.
//
// ClockIn sessions lapse server-side after ~60 min of *sliding* inactivity
// (any successful request resets the window). The browser can't read the
// session cookie or its expiry, so the server hands the client a remaining
// TTL (header on every /api response + a dedicated /api/session/ttl). This
// module holds the bits that don't need React — the countdown formatter, the
// warn/expiry thresholds, the localStorage draft schema, and the small piece
// of phase logic — so they can be unit-tested in isolation. The stateful
// wiring lives in App.jsx; the four surfaces live in SessionSurfaces.jsx.

// The single phase the app shell tracks:
//   active | warning | expired | reauth | restored | network
export const SESSION_PHASES = Object.freeze([
  'active',
  'warning',
  'expired',
  'reauth',
  'restored',
  'network',
]);

// Show the proactive nudge once remaining TTL drops to this many seconds.
export const WARN_THRESHOLD_SECS = 300; // 5:00
// Auto-dismiss the "welcome back" banner after this long.
export const RESTORE_AUTOHIDE_MS = 6500;
// localStorage key holding the in-progress draft across the OAuth redirect.
export const SAVED_WORK_KEY = 'clockin.savedWork';
// localStorage flag set right before the redirect so the post-OAuth boot
// knows to show the "signing you back in…" overlay and rehydrate the draft.
export const REAUTH_PENDING_KEY = 'clockin.reauthPending';

// Draft surfaces that can be preserved across a re-auth. Jira is the wired
// hero (full row-level restore); the rest restore the *view* the user was on.
export const SAVED_WORK_KINDS = Object.freeze([
  'jira',
  'form',
  'calendar',
  'copyday',
]);

/** Format remaining seconds as `M:SS` (e.g. 2940 → "49:00", 5 → "0:05"). */
export function fmtCountdown(secs) {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

/**
 * Given the latest known TTL and the phase we're in, decide the next phase.
 * Pure so the countdown effect's branching is testable without timers.
 *   - active  + remaining ≤ warn  → 'warning'
 *   - warning + remaining > warn   → 'active'   (a fresh request slid the window)
 *   - remaining ≤ 0               → 'expired'
 * Any other phase (expired/reauth/restored/network) is left untouched — the
 * countdown must never yank a user out of the expired modal or re-auth.
 */
export function nextPhaseForTtl(phase, remainingSecs, warnAt = WARN_THRESHOLD_SECS) {
  const remaining = Math.max(0, Math.floor(Number(remainingSecs) || 0));
  if (phase === 'active' || phase === 'warning') {
    if (remaining <= 0) return 'expired';
    if (phase === 'active' && remaining <= warnAt) return 'warning';
    if (phase === 'warning' && remaining > warnAt) return 'active';
  }
  return phase;
}

/**
 * Linearly interpolate remaining seconds between server syncs. `syncedAt` and
 * `now` are epoch ms. Used to tick the countdown client-side between the TTL
 * values the server hands us, clamped at zero.
 */
export function remainingSeconds(syncedTtlSecs, syncedAtMs, nowMs) {
  if (syncedTtlSecs == null) return null;
  const elapsed = Math.floor((nowMs - syncedAtMs) / 1000);
  return Math.max(0, Math.floor(syncedTtlSecs) - Math.max(0, elapsed));
}

/**
 * Normalise an arbitrary capture into the saved-work descriptor we persist.
 * Returns null for anything that isn't a real draft so callers can treat the
 * "nothing in progress" branch uniformly. Drops unknown kinds.
 */
export function normalizeSavedWork(work) {
  if (!work || typeof work !== 'object') return null;
  if (!SAVED_WORK_KINDS.includes(work.kind)) return null;
  const out = {
    kind: work.kind,
    date: typeof work.date === 'string' ? work.date : null,
    dateLabel: typeof work.dateLabel === 'string' ? work.dateLabel : '',
    rows: Math.max(0, parseInt(work.rows, 10) || 0),
  };
  if (work.kind === 'jira') {
    out.ready = Math.max(0, parseInt(work.ready, 10) || 0);
    out.incomplete = Math.max(0, parseInt(work.incomplete, 10) || 0);
    // Only Jira carries row-level drafts through the redirect.
    out.drafts =
      work.drafts && typeof work.drafts === 'object' ? work.drafts : {};
  }
  return out;
}

/**
 * Persist the saved-work descriptor + set the "returning from re-auth" flag.
 * Called synchronously right before the full-page OAuth redirect. Swallows
 * storage errors (private mode / disabled) — the redirect must not be blocked.
 * Returns true on success.
 */
export function persistSavedWork(work, storage = safeStorage()) {
  if (!storage) return false;
  const normalized = normalizeSavedWork(work);
  try {
    storage.setItem(SAVED_WORK_KEY, JSON.stringify(normalized));
    storage.setItem(REAUTH_PENDING_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/** Read the persisted saved-work descriptor, or null if absent/corrupt. */
export function readSavedWork(storage = safeStorage()) {
  if (!storage) return null;
  let raw;
  try {
    raw = storage.getItem(SAVED_WORK_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    return normalizeSavedWork(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** True when the current page load is a return from the OAuth re-auth. */
export function isReauthPending(storage = safeStorage()) {
  if (!storage) return false;
  try {
    return storage.getItem(REAUTH_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

/** Clear both keys once the draft has been rehydrated (or on a clean login). */
export function clearSavedWork(storage = safeStorage()) {
  if (!storage) return;
  try {
    storage.removeItem(SAVED_WORK_KEY);
    storage.removeItem(REAUTH_PENDING_KEY);
  } catch {
    /* non-fatal */
  }
}

// Resolve a usable Storage, or null where it's unavailable (SSR, private
// mode, tests). Kept internal; callers can inject their own for testing.
function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
